import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Save, Globe, Bell, Shield } from 'lucide-react';
import { toast } from 'sonner';

export default function SettingsTab({ user, refreshData, refreshUsers }) {
  const [profileData, setProfileData] = useState({
    name: '',
    age: '',
    language: 'en',
    location: '',
    timezone: 'UTC',
    religion_module: 'none'
  });

  const [modules, setModules] = useState({
    reminders: true,
    emergency: true,
    prayer_times: false,
    religious_stories: false,
    video_calls: false,
    weather: false
  });

  const [prayerSettings, setPrayerSettings] = useState({
    calculation_method: 'MWL',
    reminder_minutes_before: 10,
    adhan_enabled: false
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        age: user.age || '',
        language: user.language || 'en',
        location: user.location || '',
        timezone: user.timezone || 'UTC',
        religion_module: user.religion_module || 'none'
      });
      setModules(user.modules || modules);
      fetchPrayerSettings();
    }
  }, [user]);

  const fetchPrayerSettings = async () => {
    if (user?.modules?.prayer_times) {
      try {
        const res = await axios.get(`${API}/prayer/settings/${user.id}`);
        setPrayerSettings({
          calculation_method: res.data.calculation_method,
          reminder_minutes_before: res.data.reminder_minutes_before,
          adhan_enabled: res.data.adhan_enabled
        });
      } catch (error) {
        console.error('Error fetching prayer settings:', error);
      }
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...profileData,
        age: profileData.age ? parseInt(profileData.age) : null,
        modules: modules
      };

      await axios.put(`${API}/users/${user.id}`, payload);
      toast.success('Profile updated successfully!');
      await refreshUsers();
      await refreshData();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    }
  };

  const handlePrayerSettingsUpdate = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(`${API}/prayer/settings/${user.id}`, prayerSettings);
      toast.success('Prayer settings updated!');
    } catch (error) {
      console.error('Error updating prayer settings:', error);
      toast.error('Failed to update prayer settings');
    }
  };

  const handleModuleToggle = (moduleName, value) => {
    setModules({ ...modules, [moduleName]: value });
  };

  return (
    <div data-testid="settings-tab" className="space-y-6">
      {/* Profile Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Profile Settings
          </CardTitle>
          <CardDescription>Update user profile information</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="profile-name">Name</Label>
                <Input
                  data-testid="profile-name-input"
                  id="profile-name"
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="profile-age">Age</Label>
                <Input
                  data-testid="profile-age-input"
                  id="profile-age"
                  type="number"
                  value={profileData.age}
                  onChange={(e) => setProfileData({ ...profileData, age: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="profile-language">Language</Label>
                <Select
                  value={profileData.language}
                  onValueChange={(value) => setProfileData({ ...profileData, language: value })}
                >
                  <SelectTrigger data-testid="profile-language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="ar">Arabic</SelectItem>
                    <SelectItem value="de">German</SelectItem>
                    <SelectItem value="tr">Turkish</SelectItem>
                    <SelectItem value="ur">Urdu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="profile-location">Location</Label>
                <Input
                  data-testid="profile-location-input"
                  id="profile-location"
                  value={profileData.location}
                  onChange={(e) => setProfileData({ ...profileData, location: e.target.value })}
                  placeholder="City name"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="profile-religion">Religious Module</Label>
              <Select
                value={profileData.religion_module}
                onValueChange={(value) => {
                  setProfileData({ ...profileData, religion_module: value });
                  if (value === 'islam') {
                    setModules({ ...modules, prayer_times: true, religious_stories: true });
                  } else {
                    setModules({ ...modules, prayer_times: false, religious_stories: false });
                  }
                }}
              >
                <SelectTrigger data-testid="profile-religion-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="islam">Islamic</SelectItem>
                  <SelectItem value="christian">Christian (Coming Soon)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button data-testid="save-profile-btn" type="submit" className="w-full">
              <Save className="w-4 h-4 mr-2" />
              Save Profile Changes
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Module Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Active Modules
          </CardTitle>
          <CardDescription>Enable or disable features for this user</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Reminders</p>
              <p className="text-sm text-gray-500">Medication and routine reminders</p>
            </div>
            <Switch
              data-testid="module-reminders-switch"
              checked={modules.reminders}
              onCheckedChange={(checked) => handleModuleToggle('reminders', checked)}
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Emergency Calling</p>
              <p className="text-sm text-gray-500">Voice-activated emergency alerts</p>
            </div>
            <Switch
              data-testid="module-emergency-switch"
              checked={modules.emergency}
              onCheckedChange={(checked) => handleModuleToggle('emergency', checked)}
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Prayer Times</p>
              <p className="text-sm text-gray-500">Islamic prayer times and reminders</p>
            </div>
            <Switch
              data-testid="module-prayer-switch"
              checked={modules.prayer_times}
              onCheckedChange={(checked) => handleModuleToggle('prayer_times', checked)}
              disabled={profileData.religion_module !== 'islam'}
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Religious Content</p>
              <p className="text-sm text-gray-500">Quran verses and spiritual stories</p>
            </div>
            <Switch
              data-testid="module-religious-stories-switch"
              checked={modules.religious_stories}
              onCheckedChange={(checked) => handleModuleToggle('religious_stories', checked)}
              disabled={profileData.religion_module !== 'islam'}
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Weather Updates</p>
              <p className="text-sm text-gray-500">Daily weather information</p>
            </div>
            <Switch
              data-testid="module-weather-switch"
              checked={modules.weather}
              onCheckedChange={(checked) => handleModuleToggle('weather', checked)}
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Video Calls</p>
              <p className="text-sm text-gray-500 flex items-center gap-1">
                Video calling with family
                <Badge variant="secondary" className="text-xs">Coming Soon</Badge>
              </p>
            </div>
            <Switch
              data-testid="module-video-calls-switch"
              checked={modules.video_calls}
              onCheckedChange={(checked) => handleModuleToggle('video_calls', checked)}
              disabled
            />
          </div>
        </CardContent>
      </Card>

      {/* Prayer Settings (if Islamic module is active) */}
      {modules.prayer_times && profileData.religion_module === 'islam' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Prayer Settings
            </CardTitle>
            <CardDescription>Configure Islamic prayer time preferences</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePrayerSettingsUpdate} className="space-y-4">
              <div>
                <Label htmlFor="calc-method">Calculation Method</Label>
                <Select
                  value={prayerSettings.calculation_method}
                  onValueChange={(value) => setPrayerSettings({ ...prayerSettings, calculation_method: value })}
                >
                  <SelectTrigger data-testid="prayer-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MWL">Muslim World League</SelectItem>
                    <SelectItem value="ISNA">Islamic Society of North America</SelectItem>
                    <SelectItem value="Egypt">Egyptian General Authority</SelectItem>
                    <SelectItem value="Makkah">Umm Al-Qura, Makkah</SelectItem>
                    <SelectItem value="Karachi">University of Islamic Sciences, Karachi</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="reminder-before">Reminder Before Prayer (minutes)</Label>
                <Input
                  data-testid="prayer-reminder-input"
                  id="reminder-before"
                  type="number"
                  min="0"
                  max="60"
                  value={prayerSettings.reminder_minutes_before}
                  onChange={(e) => setPrayerSettings({ ...prayerSettings, reminder_minutes_before: parseInt(e.target.value) })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Enable Adhan Sound</p>
                  <p className="text-sm text-gray-500">Play call to prayer notification</p>
                </div>
                <Switch
                  data-testid="adhan-switch"
                  checked={prayerSettings.adhan_enabled}
                  onCheckedChange={(checked) => setPrayerSettings({ ...prayerSettings, adhan_enabled: checked })}
                />
              </div>

              <Button data-testid="save-prayer-settings-btn" type="submit" className="w-full">
                <Save className="w-4 h-4 mr-2" />
                Save Prayer Settings
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}